import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useContext, useEffect, useState } from 'react';
import { ELEMENT_TIMEOUT } from '../../common/constants/timeouts';
import { calcTextFieldActionSubjectId } from '../../common/utils/action-subject-id';
import { createFieldCreditCardStripeError } from '../../common/utils/errors';
import { useUIEventDispatch } from '../../common/utils/events';
import { formContext } from '../context';
import { useFieldRegisterHook } from '../hooks/use-credit-card-field';
import { useFieldErrorState } from '../hooks/use-validation-error-state';
export var useReactFieldState = function useReactFieldState(_ref) {
  var name = _ref.name,
    onParentReady = _ref.onReady;
  var dispatchAnalyticsEvent = useUIEventDispatch();
  var _useContext = useContext(formContext),
    fieldConfigs = _useContext.fieldConfigs;
  var fieldCallbacks = fieldConfigs !== undefined ? fieldConfigs[name] : undefined;
  var createAndFireCreditCardEvent = useCallback(function (action, actionSubjectId) {
    dispatchAnalyticsEvent({
      action: action,
      actionSubject: 'textField',
      actionSubjectId: actionSubjectId
    });
  }, [dispatchAnalyticsEvent]);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    element = _useState2[0],
    setElement = _useState2[1];
  var _useFieldErrorState = useFieldErrorState(name),
    failureMessage = _useFieldErrorState.failureMessage,
    setFailure = _useFieldErrorState.setFailure;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    focused = _useState4[0],
    setFocused = _useState4[1];
  var onFocus = useCallback(function () {
    setFocused(true);
    createAndFireCreditCardEvent('focused', calcTextFieldActionSubjectId(name));
    if (fieldCallbacks !== undefined && fieldCallbacks.onFocus !== undefined) {
      fieldCallbacks.onFocus();
    }
  }, [createAndFireCreditCardEvent, setFocused, name, fieldCallbacks]);
  var onBlur = useCallback(function () {
    setFocused(false);
    createAndFireCreditCardEvent('blurred', calcTextFieldActionSubjectId(name));
    if (fieldCallbacks !== undefined && fieldCallbacks.onBlur !== undefined) {
      fieldCallbacks.onBlur();
    }
  }, [setFocused, name, createAndFireCreditCardEvent, fieldCallbacks]);
  var onChange = useCallback(function (originalPayload) {
    var payload = fieldCallbacks !== undefined && fieldCallbacks.onChange !== undefined ? fieldCallbacks.onChange(originalPayload) : originalPayload;
    var error = payload.error;
    if (error !== undefined) {
      setFailure(createFieldCreditCardStripeError(error, name));
    } else {
      setFailure(undefined);
    }
  }, [name, setFailure, fieldCallbacks]);
  var focus = useCallback(function () {
    if (element) {
      element.focus();
    }
  }, [element]);
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    failed = _useState6[0],
    setFailed = _useState6[1];
  var onReady = useCallback(function (element) {
    setElement(element);
    if (onParentReady) {
      onParentReady(element);
    }
  }, [onParentReady]);
  var isReady = Boolean(element);
  useEffect(function () {
    if (!isReady) {
      var tm = setTimeout(function () {
        // eslint-disable-next-line no-console
        console.error("StripeElement ".concat(name, " is not ready after ").concat(ELEMENT_TIMEOUT, " timeout"));
        setFailed(true);
      }, ELEMENT_TIMEOUT);
      return function () {
        return clearTimeout(tm);
      };
    }
  }, [isReady, name]);
  useFieldRegisterHook(name, setFailure, focus, isReady, failed);
  return {
    state: {
      focused: focused,
      error: failureMessage
    },
    callbacks: {
      onFocus: onFocus,
      onBlur: onBlur,
      onChange: onChange,
      onReady: onReady
    }
  };
};